import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

/**
 * SummaryCard - Card individual de KPI (mesmo visual de CompanySummaryCards)
 */
function SummaryCard({ title, value, icon, iconColor, iconBg }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, fontSize: '0.8125rem' }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary', lineHeight: 1 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 20 } })}
        </Box>
      </Box>
    </Card>
  );
}

/**
 * FeedbackSummaryCards - Cards de KPI da aba "Feedbacks" (Admin Geral)
 * @param {Object} props
 * @param {{ total: number, problems: number, improvements: number, received: number, triaged: number,
 *           fixed: number, released: number, answered: number }} props.summary
 */
function FeedbackSummaryCards({ summary }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const s = summary || { total: 0, problems: 0, improvements: 0, received: 0, triaged: 0, fixed: 0, released: 0, answered: 0 };

  const cards = [
    { title: 'Total', value: s.total, icon: <FeedbackOutlinedIcon />, iconColor: '#5A80FB', iconBg: 'rgba(90, 128, 251, 0.1)' },
    { title: 'Problemas', value: s.problems, icon: <ReportProblemOutlinedIcon />, iconColor: '#E65100', iconBg: '#FFF3E0' },
    { title: 'Melhorias', value: s.improvements, icon: <LightbulbOutlinedIcon />, iconColor: '#1565C0', iconBg: '#E3F2FD' },
    { title: 'A analisar', value: s.received, icon: <FiberNewOutlinedIcon />, iconColor: '#616161', iconBg: '#F5F5F5' },
    { title: 'Aguardando deploy', value: s.fixed, icon: <BuildOutlinedIcon />, iconColor: '#1565C0', iconBg: '#E3F2FD' },
    { title: 'Concluídos', value: s.released + s.answered, icon: <CheckCircleOutlineOutlinedIcon />, iconColor: '#2E7D32', iconBg: '#E8F5E9' },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)',
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card, index) => (
        <SummaryCard key={index} {...card} />
      ))}
    </Box>
  );
}

export default FeedbackSummaryCards;
